import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, Activity, Target, TrendingUp, Zap, ArrowRight, Clock, FileText, Sparkles, Share2, CalendarDays } from 'lucide-react';
import { api, DOMAIN_SHORT_NAMES, getScoreLevel, BELIEF_ARCHITECTURE_PATTERNS } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import MindWheel from '../components/visualizations/MindWheel';

export default function Dashboard() {
  const [latestReport, setLatestReport] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [nextSchedule, setNextSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportRes, pathwaysRes, scheduleRes] = await Promise.all([
          api.get('/reports/latest'),
          api.get('/pathways'),
          api.get('/schedule/assessments')
        ]);
        setLatestReport(reportRes.data);
        setPathways(pathwaysRes.data.pathways || []);
        const upcoming = (scheduleRes.data.schedules || []).filter(
          s => s.status === 'scheduled' && new Date(s.scheduled_date) > new Date()
        );
        setNextSchedule(upcoming[0] || null);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // New user - no assessment yet
  if (!latestReport) {
    return <NewUserDashboard />;
  }

  // Support both old and new field names
  const stabilityIndex = latestReport.stability_index || latestReport.mind_index || 50;
  const neuralFriction = latestReport.neural_friction_score ?? latestReport.cognitive_risk_index ?? 0;
  const pattern = latestReport.belief_architecture_pattern || latestReport.cluster_profile;
  
  const stabilityLevel = getScoreLevel(100 - stabilityIndex);
  const patternInfo = BELIEF_ARCHITECTURE_PATTERNS[pattern?.primary_cluster];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Stability Index</p>
                  <p className="text-4xl font-bold mt-2 font-mono" style={{ color: stabilityLevel.color }}>
                    {stabilityIndex.toFixed(1)}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{stabilityLevel.label}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Neural Friction</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-amber-500">
                    {neuralFriction}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Domains with friction</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Strengths</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-emerald-500">
                    {latestReport.top_strengths?.length || 0}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Areas of stability</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Active Modules</p>
                  <p className="text-4xl font-bold mt-2 font-mono text-primary">
                    {pathways.filter(p => p.status === 'active').length}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">In progress</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Mind Architecture Wheel */}
        <motion.div
          className="md:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card border-border/50 h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Mind Architecture</CardTitle>
                <Link to="/mind-map">
                  <Button variant="ghost" size="sm" data-testid="view-mind-map">
                    View Full Map
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <MindWheel domainScores={latestReport.domain_scores} />
            </CardContent>
          </Card>
        </motion.div>

        {/* Belief Architecture Pattern */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-card border-border/50 h-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Belief Architecture</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {patternInfo && (
                <div className="space-y-4">
                  <div 
                    className="h-16 w-16 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${patternInfo.color}20` }}
                  >
                    <Brain className="h-8 w-8" style={{ color: patternInfo.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{patternInfo.name}</h3>
                    {patternInfo.supporting && (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs font-mono text-muted-foreground">Supporting Patterns:</p>
                        {patternInfo.supporting.map((p, i) => (
                          <p key={i} className="text-sm text-muted-foreground">• {p}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Link to="/report">
                <Button className="w-full" variant="outline" data-testid="view-full-report">
                  <FileText className="mr-2 h-4 w-4" />
                  View Full Report
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Priority Focus & Upgrade Modules */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 30-Day Focus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <CardTitle className="text-lg font-semibold">30-Day Focus</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {latestReport.thirty_day_focus}
              </p>
              <Link to="/upgrade-path" className="block mt-4">
                <Button variant="outline" className="w-full" data-testid="view-upgrade-path">
                  View Upgrade Modules
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>

        {/* Neural Friction Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                <CardTitle className="text-lg font-semibold">Priority Optimization</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {latestReport.top_bottlenecks?.slice(0, 3).map((bottleneck, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                    <span className="text-xs font-mono text-muted-foreground">0{index + 1}</span>
                    <span className="text-sm">{bottleneck}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Upcoming Schedule */}
      {nextSchedule && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
        >
          <Card className="bg-card border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <CalendarDays className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-mono text-muted-foreground uppercase">Next Scheduled Assessment</p>
                    <p className="font-semibold">
                      {nextSchedule.assessment_type === 'full' ? 'Full Diagnostic' : 'Express Scan'}
                      {' — '}
                      {new Date(nextSchedule.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <Link to="/schedule">
                  <Button variant="outline" size="sm" data-testid="view-schedule-btn">
                    View Schedule
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold">Track Your Evolution</h3>
                  <p className="text-sm text-muted-foreground">Retake assessment or share your progress</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Link to="/progress">
                  <Button variant="outline" data-testid="share-progress-btn">
                    <Share2 className="mr-2 h-4 w-4" />
                    Share Progress
                  </Button>
                </Link>
                <Link to="/assessment/full">
                  <Button className="glow-primary" data-testid="full-diagnostic-btn">
                    New Assessment
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function NewUserDashboard() {
  return (
    <div className="space-y-8" data-testid="new-user-dashboard">
      <motion.div
        className="text-center max-w-2xl mx-auto py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Brain className="h-12 w-12 text-primary" strokeWidth={1.5} />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
          Welcome to MindFlō
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
          Your Human Mental Operating System. Map your cognitive architecture, 
          identify neural friction points, and follow structured upgrade pathways.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-card border-border/50 hover:border-primary/50 transition-colors group">
          <CardContent className="p-8">
            <div className="h-14 w-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:bg-emerald-500/20 transition-colors">
              <Zap className="h-7 w-7 text-emerald-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Quick Mind Scan</h2>
            <p className="text-muted-foreground mb-6">
              15 questions • ~3 minutes
              <br />
              Get a quick overview of your mental patterns
            </p>
            <Link to="/assessment/express">
              <Button variant="outline" className="w-full" data-testid="start-express-scan">
                Start Quick Scan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-card border-primary/50 relative overflow-hidden group">
          <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            Recommended
          </div>
          <CardContent className="p-8">
            <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
              <Brain className="h-7 w-7 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Full Mind Diagnostic</h2>
            <p className="text-muted-foreground mb-6">
              100 questions • ~15 minutes
              <br />
              Complete mental architecture mapping
            </p>
            <Link to="/assessment/full">
              <Button className="w-full glow-primary" data-testid="start-full-diagnostic">
                Start Full Diagnostic
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </motion.div>

      {/* Seven Domains Preview */}
      <motion.div
        className="max-w-4xl mx-auto pt-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h3 className="text-center text-lg font-semibold mb-6">Seven Diagnostic Domains</h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {Object.values(DOMAIN_SHORT_NAMES).map((name, index) => (
            <div 
              key={name}
              className="p-3 rounded-lg bg-card border border-border/50 text-center"
            >
              <span className="text-xs font-mono text-muted-foreground block mb-1">0{index + 1}</span>
              <span className="text-xs font-medium">{name}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
